import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import LazyAvatar from "@/components/LazyAvatar";
import { MagnifyingGlass, ChatCircleDots, HandWaving } from "@phosphor-icons/react";
import { toast } from "sonner";

const firstName = (full) => (full || "").trim().split(/\s+/)[0] || "there";

export default function Networking() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [attendees, setAttendees] = useState([]);
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [sendingTo, setSendingTo] = useState(null); // user id currently being said-hi-to

  const threadUserIds = useMemo(() => new Set(threads.map((t) => t.user.id)), [threads]);

  const load = async () => {
    if (!user?.networking_opt_in) {
      try {
        const t = await api.get(`/networking/threads`);
        setThreads(t.data);
      } catch (err) { console.warn("[networking] threads load failed:", err); }
      setAttendees([]);
      return;
    }
    try {
      const [a, t] = await Promise.all([
        api.get(`/networking/attendees${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get(`/networking/threads`),
      ]);
      setAttendees(a.data); setThreads(t.data);
    } catch (err) { console.warn("[networking] load failed:", err); }
  };
  useEffect(() => { load(); }, [user, q]);

  const sayHi = async (e, attendee) => {
    e.preventDefault();
    e.stopPropagation();
    if (sendingTo) return;
    setSendingTo(attendee.id);
    const text = `Hi ${firstName(attendee.name)}! 👋`;
    try {
      await api.post(`/networking/messages`, { to_user_id: attendee.id, text });
      // Optimistically register a thread so the button switches to the chat icon.
      setThreads((prev) => [
        { user: attendee, last_message: { text, created_at: new Date().toISOString() } },
        ...prev.filter((t) => t.user.id !== attendee.id),
      ]);
      toast.success(`Sent "Hi ${firstName(attendee.name)}! 👋"`);
      navigate(`/messages/${attendee.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <div data-testid="networking-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1">Meet people</div>
          <h1 className="font-display text-3xl tracking-tight">Networking</h1>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="opt" checked={!!user?.networking_opt_in} onCheckedChange={async (v) => { await updateProfile({ networking_opt_in: v }); }} data-testid="networking-toggle" />
          <Label htmlFor="opt" className="text-xs text-[#5a5a5a]">Visible</Label>
        </div>
      </div>

      {!user?.networking_opt_in && (
        <div data-testid="networking-hidden-banner" className="mb-4 p-3 bg-[#000000]/5 border border-[#000000]/20 rounded-sm text-sm text-[#1c1c24]">
          You&apos;re currently hidden. Flip <strong>Visible</strong> back on to appear in the directory and receive new messages.{" "}
          <Link to="/info/networking" className="underline">How this works</Link>.
        </div>
      )}

      {threads.length > 0 && (
        <div className="mb-5">
          <h2 className="font-mono-time text-xs uppercase tracking-widest text-[#7A828C] mb-2">Conversations</h2>
          <div className="space-y-2">
            {threads.map((t) => (
              <Link key={t.user.id} to={`/messages/${t.user.id}`} data-testid={`thread-${t.user.id}`}
                className="flex items-center gap-3 p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors">
                <LazyAvatar
                  endpoint={`/users/${t.user.id}/photo`}
                  hasPhoto={t.user.has_photo}
                  name={t.user.name}
                  className="w-10 h-10 rounded-full flex-shrink-0"
                  data-testid={`thread-avatar-${t.user.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.user.name}</div>
                  <div className="text-xs text-[#7A828C] truncate">{t.last_message.text}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {user?.networking_opt_in && (
        <>
          <div className="relative mb-3">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A828C]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, org, role…"
              className="w-full h-12 pl-10 pr-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm focus:border-[#000000] outline-none" data-testid="networking-search" />
          </div>

          <h2 className="font-mono-time text-xs uppercase tracking-widest text-[#7A828C] mb-2">Delegates</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {attendees.map((a) => {
              const hasThread = threadUserIds.has(a.id);
              const isSending = sendingTo === a.id;
              return (
                <Link
                  key={a.id}
                  to={`/messages/${a.id}`}
                  data-testid={`attendee-${a.id}`}
                  aria-label={`Message ${a.name}`}
                  className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3 hover:border-[#000000] hover:-translate-y-[1px] transition-all"
                >
                  <LazyAvatar
                    endpoint={`/users/${a.id}/photo`}
                    hasPhoto={a.has_photo}
                    name={a.name}
                    className="w-12 h-12 rounded-full flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="text-xs text-[#7A828C] truncate">{a.role_title}{a.org ? ` · ${a.org}` : ""}</div>
                  </div>
                  {hasThread ? (
                    <div className="w-10 h-10 grid place-items-center rounded-sm border border-[#E5E9F0] flex-shrink-0" data-testid={`message-${a.id}`} aria-hidden="true">
                      <ChatCircleDots size={18} className="text-[#000000]" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => sayHi(e, a)}
                      disabled={isSending}
                      data-testid={`say-hi-${a.id}`}
                      aria-label={`Say hi to ${a.name}`}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-3 rounded-sm bg-[#000000] text-[#FFFFFF] text-xs font-semibold uppercase tracking-wider hover:bg-[#1c1c24] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      <HandWaving size={14} weight="fill" />
                      <span>{isSending ? "…" : "Say hi"}</span>
                    </button>
                  )}
                </Link>
              );
            })}
            {attendees.length === 0 && <div className="text-[#7A828C] col-span-full text-center py-8">No delegates yet.</div>}
          </div>
        </>
      )}
    </div>
  );
}
